import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    Injector,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FiDialogsContentBase, FI_DIALOGS_CONTENT_DATA } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { FiDialogsContainerBase } from '@fi-shared/fi-dialogs/fi-dialogs-container-base';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { isDefined, fiGetLocaleCode } from '@fi-shared/utils';
import { MAT_DATE_RANGE_SELECTION_STRATEGY } from '@angular/material/datepicker';
import { DefaultMatCalendarRangeStrategy } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { DateAdapter } from '@angular/material/core';
import { FiUserDataService } from '@fi-app/shared/fi-shared-services/fi-user-data.service';
import { FiDateAdapter, FI_FORMATS } from '@fi-shared/fi-date-picker/fi-date-adapter';

interface IDateTImePickerDialogData {
    date: Date;
    minDate?: Date;
}

@Component({
    selector: 'fi-date-time-picker-dialog',
    templateUrl: './fi-date-time-picker-dialog.component.html',
    styleUrls: ['./fi-date-time-picker-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-date-time-picker-dialog' },
    standalone: false,
    providers: [
        {
            provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
            useClass: DefaultMatCalendarRangeStrategy,
        },
        { provide: DateAdapter, useClass: FiDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: FI_FORMATS },
    ],
})
export class FiDateTimePickerDialogComponent
    extends FiDialogsContentBase<IDateTImePickerDialogData, Date>
    implements OnInit
{
    @ViewChild(FiValidatingFormDirective) dateTimePickerForm: FiValidatingFormDirective;

    date: Date;
    hour: number;
    minute: number;

    readonly maxMinutes: number = 59;
    readonly maxHours: number = 23;

    minDate: Date;
    minHours: number;
    minMinutes: number;

    constructor(
        dialogRef: MatDialogRef<FiDialogsContainerBase<IDateTImePickerDialogData, Date>>,
        @Inject(FI_DIALOGS_CONTENT_DATA) data: IDateTImePickerDialogData,
        injector: Injector,
        private adapter: DateAdapter<any>,
        private fiUserDataService: FiUserDataService,
    ) {
        super(dialogRef, data, injector);
        const isoCode = fiGetLocaleCode(this.fiUserDataService.applicationLanguage);
        adapter.setLocale(isoCode);
    }

    ngOnInit() {
        this.setDateData();
        this.setServices();
    }

    private setDateData() {
        this.date = new Date(this.data.date);
        this.hour = this.date.getHours();
        this.minute = this.date.getMinutes();

        if (isDefined(this.data.minDate)) {
            this.minDate = new Date(this.data.minDate);
            this.onDateSelectionChange();
        }
    }

    onDateSelectionChange() {
        if (isDefined(this.minDate)) {
            const tempMinDate: Date = new Date(this.minDate);
            tempMinDate.setHours(this.date.getHours());
            tempMinDate.setMinutes(this.date.getMinutes());
            tempMinDate.setSeconds(this.date.getSeconds());
            tempMinDate.setMilliseconds(this.date.getMilliseconds());
            if (tempMinDate.getTime() === this.date.getTime()) {
                this.setMinTime();
            } else {
                this.setInitialHoursMinutes();
            }
        }

        if (isDefined(this.dateTimePickerForm)) this.updateFormValidity();
    }

    private setInitialHoursMinutes() {
        this.minHours = 0;
        this.minMinutes = 0;
        this.hour = this.date.getHours();
        this.minute = this.date.getMinutes();
    }

    private setMinTime() {
        this.minHours = this.minDate.getHours();
        this.minMinutes = this.minDate.getMinutes();
        this.hour = this.minHours;
        this.minute = this.minMinutes;
    }

    onHourChange() {
        this.minMinutes = this.hour === this.minHours ? this.minDate.getMinutes() : 0;
        this.updateFormValidity();
    }

    updateFormValidity() {
        this.dateTimePickerForm.updateValidity();
    }

    isYesButtonDisabled(): boolean {
        return !!this.dateTimePickerForm.errors || this.dateTimePickerForm.pending;
    }

    getResult(): Date {
        this.date.setHours(this.hour, this.minute);
        return this.date;
    }

    private fiDialogsService: FiDialogsService;
    private appTextService: AppTextService;
    private setServices() {
        this.fiDialogsService = this.injector.get(FiDialogsService);
        this.appTextService = this.injector.get(AppTextService);
    }
}
